import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelectionStore } from "../../stores/selectionStore";
import { INSPECTOR_ADD_NO_REVEAL_SOURCE } from "../../utils/focusNewEntityInInspector";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { useInspectorChromeFlash } from "../../hooks/useInspectorChromeFlash";

export function useInspectorPanelVisibility({
  activePanel,
  isMobile,
  lastSelectionSource,
  pendingVisualMeshParentBodyUuid,
  selectionSequence,
  selectedActuator,
  selectedBody,
  selectedCamera,
  selectedEquality,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedLight,
  selectedSite,
  selectedTendon,
  setPendingVisualMeshParentBodyUuid,
}) {
  const [minimized, setMinimized] = useState(true);
  const [mobileScenePeekExpanded, setMobileScenePeekExpanded] = useState(true);
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;
  const { inspectorChromeFlashRootClass } = useInspectorChromeFlash({ minimizedRef, setMinimized });
  const activePanelRef = useRef(null);
  activePanelRef.current = activePanel;
  const [tabAttentionAnimating, setTabAttentionAnimating] = useState(false);
  const minimizedTabAttentionAtRef = useRef(0);
  const minimizedTabAttentionTimeoutRef = useRef(null);

  const triggerMinimizedTabAttention = useCallback(() => {
    if (!isMobile) return;
    if (!minimizedRef.current || !activePanelRef.current) return;
    const now = Date.now();
    if (now - minimizedTabAttentionAtRef.current < 450) return;
    minimizedTabAttentionAtRef.current = now;
    setTabAttentionAnimating(false);
    window.clearTimeout(minimizedTabAttentionTimeoutRef.current);
    requestAnimationFrame(() => {
      setTabAttentionAnimating(true);
      minimizedTabAttentionTimeoutRef.current = window.setTimeout(() => {
        setTabAttentionAnimating(false);
        minimizedTabAttentionTimeoutRef.current = null;
      }, 700);
    });
  }, [isMobile]);

  useEffect(() => {
    const handleStartVisualMeshCreation = (event) => {
      const parentBodyUuid = event?.detail?.parentBodyUuid || null;
      if (!parentBodyUuid) return;
      const selection = useSelectionStore.getState();
      selection.setLastSelectionSource("sceneTree");
      selection.clearAllSelections();
      setPendingVisualMeshParentBodyUuid(parentBodyUuid);
      setMinimized(false);
      requestInspectorChromeFlash();
    };
    window.addEventListener("start-visual-mesh-creation", handleStartVisualMeshCreation);
    return () =>
      window.removeEventListener("start-visual-mesh-creation", handleStartVisualMeshCreation);
  }, [setPendingVisualMeshParentBodyUuid]);

  useEffect(() => {
    if (!pendingVisualMeshParentBodyUuid) return;
    if (
      selectedBody ||
      selectedGeom ||
      selectedJoint ||
      selectedActuator ||
      selectedSite ||
      selectedInertial ||
      selectedEquality ||
      selectedTendon ||
      selectedCamera ||
      selectedLight
    ) {
      setPendingVisualMeshParentBodyUuid(null);
    }
  }, [
    pendingVisualMeshParentBodyUuid,
    selectedBody,
    selectedGeom,
    selectedJoint,
    selectedActuator,
    selectedSite,
    selectedInertial,
    selectedEquality,
    selectedTendon,
    selectedCamera,
    selectedLight,
    setPendingVisualMeshParentBodyUuid,
  ]);

  const mobileScenePeekCompact =
    isMobile &&
    !mobileScenePeekExpanded &&
    (lastSelectionSource === "scene" ||
      lastSelectionSource === "sceneClick" ||
      lastSelectionSource === "sceneTree" ||
      lastSelectionSource === "sceneTreeObject");

  const showMobilePeekSettingsFooter =
    isMobile &&
    (lastSelectionSource === "scene" ||
      lastSelectionSource === "sceneClick" ||
      lastSelectionSource === "sceneTree" ||
      lastSelectionSource === "sceneTreeObject");

  useLayoutEffect(() => {
    if (!isMobile) {
      setMobileScenePeekExpanded(true);
      return;
    }
    const fromViewport = lastSelectionSource === "scene" || lastSelectionSource === "sceneClick";
    if (fromViewport) {
      setMobileScenePeekExpanded(false);
      return;
    }
    const fromInspectorTreeNav =
      lastSelectionSource === "sceneTree" || lastSelectionSource === "sceneTreeObject";
    if (fromInspectorTreeNav) {
      return;
    }
    setMobileScenePeekExpanded(true);
  }, [isMobile, selectionSequence, lastSelectionSource]);

  useLayoutEffect(() => {
    if (!isMobile || !minimized) return;
    if (lastSelectionSource === "scene" || lastSelectionSource === "sceneClick") {
      setMobileScenePeekExpanded(false);
    }
  }, [minimized, isMobile, lastSelectionSource]);

  useEffect(() => {
    const shouldAutoRevealInspector =
      lastSelectionSource === "scene" ||
      lastSelectionSource === "sceneClick" ||
      lastSelectionSource === "sceneTree" ||
      lastSelectionSource === "sceneTreeObject" ||
      lastSelectionSource === "objectLibrary";

    if (shouldAutoRevealInspector && activePanel) {
      if (isMobile) {
        const tappedPickInScene =
          lastSelectionSource === "scene" || lastSelectionSource === "sceneClick";
        if (tappedPickInScene) {
          setMinimized(false);
        } else {
          triggerMinimizedTabAttention();
        }
      } else {
        setMinimized(false);
      }
    } else if (shouldAutoRevealInspector && !activePanel) {
      setMinimized(true);
    }
  }, [selectionSequence, lastSelectionSource, activePanel, isMobile, triggerMinimizedTabAttention]);

  useEffect(() => {
    if (
      !selectedJoint &&
      !selectedActuator &&
      !selectedEquality &&
      !selectedTendon &&
      !selectedCamera &&
      !selectedLight
    )
      return;
    if (lastSelectionSource === INSPECTOR_ADD_NO_REVEAL_SOURCE) return;
    if (isMobile) {
      if (lastSelectionSource === "scene" || lastSelectionSource === "sceneClick") {
        return;
      }
      triggerMinimizedTabAttention();
      return;
    }
    setMinimized(false);
  }, [
    selectedJoint,
    selectedActuator,
    selectedEquality,
    selectedTendon,
    selectedCamera,
    selectedLight,
    lastSelectionSource,
    isMobile,
    triggerMinimizedTabAttention,
  ]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("properties-panel-minimized-change", { detail: { minimized } })
    );
  }, [minimized]);

  useEffect(() => {
    const respond = () => {
      window.dispatchEvent(
        new CustomEvent("properties-panel-minimized-change", {
          detail: { minimized: minimizedRef.current },
        })
      );
    };
    window.addEventListener("request-properties-panel-minimized-state", respond);
    return () => window.removeEventListener("request-properties-panel-minimized-state", respond);
  }, []);

  useEffect(() => {
    const handleOpen = () => setMinimized(false);
    const handleClose = () => setMinimized(true);
    window.addEventListener("open-properties-panel", handleOpen);
    window.addEventListener("close-properties-panel", handleClose);
    return () => {
      window.removeEventListener("open-properties-panel", handleOpen);
      window.removeEventListener("close-properties-panel", handleClose);
    };
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(minimizedTabAttentionTimeoutRef.current);
    };
  }, []);

  const handleMinimize = useCallback(() => {
    setMinimized(true);
  }, []);

  return {
    handleMinimize,
    inspectorChromeFlashRootClass,
    minimized,
    mobileScenePeekCompact,
    setMobileScenePeekExpanded,
    setMinimized,
    showMobilePeekSettingsFooter,
    tabAttentionAnimating,
  };
}
